import * as React from "react"
import PhoneInput from "react-phone-number-input"
import { isValidPhoneNumber, getCountryCallingCode } from "react-phone-number-input"
import type { CountryCode } from "react-phone-number-input"
import flags from "react-phone-number-input/flags"
import "react-phone-number-input/style.css"
import { cn } from "@/lib/utils"

export interface PhoneInputProps {
  value?: string
  onChange?: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  defaultCountry?: CountryCode
  onValidationChange?: (isValid: boolean) => void
  showError?: boolean
}



const PhoneInputComponent = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ 
    className, 
    defaultCountry = "GB" as CountryCode, 
    onChange, 
    onValidationChange,
    showError = false,
    value,
    disabled,
    placeholder,
    ...props 
  }, _ref) => {
    const [showValidation, setShowValidation] = React.useState(false)
    const [hasBlurred, setHasBlurred] = React.useState(false)

    // Handle phone number change with automatic normalization
    const handleChange = (phoneValue: string | undefined) => {
      // The library automatically handles leading zero removal and E.164 formatting
      const isValid = !phoneValue || isValidPhoneNumber(phoneValue)
      
      // Notify parent of validation state
      onValidationChange?.(isValid)
      
      // Only show validation if user has interacted or showError is true
      if (hasBlurred || showError) {
        setShowValidation(!isValid && !!phoneValue)
      }
      
      onChange?.(phoneValue)
    }

    // Handle blur event to start showing validation
    const handleBlur = React.useCallback(() => {
      setHasBlurred(true)
      if (value && !isValidPhoneNumber(value)) {
        setShowValidation(true)
      }
    }, [value])

    // Handle focus to hide validation while typing
    const handleFocus = React.useCallback(() => {
      setShowValidation(false)
    }, [])


    // Update validation display when showError prop changes
    React.useEffect(() => {
      if (showError && value && !isValidPhoneNumber(value)) {
        setShowValidation(true)
      }
    }, [showError, value])

    return (
      <div className="space-y-1">
        <div 
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            showValidation && "border-red-500 focus-within:ring-red-500",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          onBlur={handleBlur}
          onFocus={handleFocus}
        >
        <PhoneInput
          {...props}
          international
          defaultCountry={defaultCountry}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          className="flex w-full border-0 bg-transparent PhoneInput-custom"
          // Use library's default country selector with flags
        />
        </div>
        
        {showValidation && (
          <p className="text-xs text-red-600">Please enter a valid phone number</p>
        )}
        
        <style>{`
          /* Target the actual PhoneInputCountry div (first child of PhoneInput) */
          .PhoneInput-custom > *:first-child {
            border: none !important;
            background: hsl(var(--muted) / 0.5) !important;
            border-right: 1px solid hsl(var(--border)) !important;
            border-radius: 0.375rem 0 0 0.375rem !important;
            padding-left: 1rem !important;
            padding-right: 0.5rem !important;
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
            height: 2.5rem !important;
            min-width: 4.5rem !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            margin-left: 0 !important;
            box-sizing: border-box !important;
          }
          
          .PhoneInput-custom .PhoneInputInput {
            border: none !important;
            outline: none !important;
            background: transparent !important;
            padding: 0.5rem 0.75rem !important;
            flex: 1 !important;
            font-size: 0.875rem !important;
            color: inherit !important;
            border-radius: 0 0.375rem 0.375rem 0 !important;
          }
          
          .PhoneInput-custom .PhoneInputInput::placeholder {
            color: hsl(var(--muted-foreground)) !important;
          }
          
          .PhoneInput-custom .PhoneInputInput:disabled {
            cursor: not-allowed !important;
          }
          
          .PhoneInput-custom > *:first-child:hover {
            background: hsl(var(--muted)) !important;
          }
          
          .PhoneInput-custom > *:first-child:focus,
          .PhoneInput-custom > *:first-child:focus-within {
            outline: 2px solid hsl(var(--ring)) !important;
            outline-offset: 2px !important;
          }
          
          .PhoneInput-custom .PhoneInputCountrySelectArrow {
            border-top-color: hsl(var(--muted-foreground)) !important;
            margin-left: 0.25rem !important;
          }
          
          /* Remove gray border around flag in button and add spacing */
          .PhoneInput-custom .PhoneInputCountryIcon {
            width: 1.25rem !important;
            height: 1rem !important;
            border: none !important;
            background: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            margin-right: 0.5rem !important;
            display: block !important;
            flex-shrink: 0 !important;
            position: relative !important;
          }
          
          /* Additional override for the country select button content */
          .PhoneInput-custom .PhoneInputCountrySelect > * {
            margin-left: 0 !important;
          }
          
          /* Style the dropdown */
          .PhoneInputCountrySelect-dropdown {
            background: hsl(var(--background)) !important;
            border: 1px solid hsl(var(--border)) !important;
            border-radius: 0.375rem !important;
            box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
            max-height: 15rem !important;
            overflow-y: auto !important;
            z-index: 50 !important;
            margin-top: 0.25rem !important;
          }
          
          .PhoneInputCountrySelect-option {
            padding: 0.5rem 0.75rem !important;
            display: flex !important;
            align-items: center !important;
            gap: 0.5rem !important;
            font-size: 0.875rem !important;
            color: hsl(var(--foreground)) !important;
            cursor: pointer !important;
            border: none !important;
            background: none !important;
          }
          
          .PhoneInputCountrySelect-option:hover {
            background: hsl(var(--muted)) !important;
          }
          
          .PhoneInputCountrySelect-option:focus {
            background: hsl(var(--muted)) !important;
            outline: none !important;
          }
          
          /* Try to fix flag display in dropdown options */
          .PhoneInputCountrySelect-option .PhoneInputCountryIcon {
            width: 1.25rem !important;
            height: 1rem !important;
            margin-right: 0.5rem !important;
            border: none !important;
            background: none !important;
            box-shadow: none !important;
            flex-shrink: 0 !important;
            display: inline-block !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* Alternative selectors for flags */
          .PhoneInputCountrySelect-option img,
          .PhoneInputCountrySelect-option svg {
            width: 1.25rem !important;
            height: 1rem !important;
            display: inline-block !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* Try broader selector */
          .PhoneInputCountrySelect-dropdown img,
          .PhoneInputCountrySelect-dropdown svg {
            width: 1.25rem !important;
            height: 1rem !important;
            display: inline-block !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
        `}</style>
      </div>
    )
  }
)

PhoneInputComponent.displayName = "PhoneInput"

export { PhoneInputComponent as PhoneInput }